import React from 'react';
import { Typography } from '@mui/material';
import { Popover, Whisper } from 'rsuite';
import { useTranslation } from 'react-i18next';
import { BreakRuleConditionTypeNames, BreakRuleActionTypeNames, RequestTypes, RequestSubtypes, RequestTypeNames, RequestSubtypeNames, JobTransferValidationType, SwapPeriodType } from '../../../Enums';
import './AdminDetailsTooltip.scss';

const setTwoDigits = (num) => `0${Math.floor(num)}`.slice(-2);

const getTimeString = (value) => {
    const hours = setTwoDigits(value);
    const minutes = setTwoDigits((value * 60) % 60);
    return `${hours}:${minutes}`;
};

const flattenObject = (obj, t, parentKey = '', result = {}) => {
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            const newKey = parentKey ? `${key}` : key;

            if (Array.isArray(obj[key])) {
                obj[key].forEach((item, index) => {
                    flattenObject(item, t, `${index}`, result);
                });
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                flattenObject(obj[key], t, newKey, result);
            } else {
                result[newKey] = obj[key];

                if ((key === 'actionType' || key === 'requestType') && RequestTypeNames[obj[key]] !== undefined) {
                    result['requestTypeName'] = t("AdminRequestSetup." + RequestTypeNames[obj[key]]);
                }

                if (key === 'requestSubtype' && result['requestTypeName']) {
                    const actionType = obj['actionType'];
                    const requestSubType = obj['requestSubtype'];
                    const subTypeLabel = RequestSubtypeNames[requestSubType];
                    
                    if (subTypeLabel || actionType === 2 || actionType === 3) {
                        result['requestSubType'] = 
                            `${subTypeLabel === 'fill' ? 'Fill ' : ''}${result['requestTypeName']}${subTypeLabel === 'signUp' ? ' Sign-up' : ''}`;
                    }
                }
            }
        }
    }
    return result;
};

const displayAwardingRuleProperties = (rule, dataviewNotFoundLabel) => {
    switch (rule.awardingRuleParameterTypeLabel) {
        case "People":
            return `${rule.awardingRuleParameterTypeLabel} - ${rule.hyperfindBelongingInfos.label} (${rule.hyperfindBelongingInfos.priorityLabel})`;
        case "Dataview":
            return `${rule.awardingRuleParameterTypeLabel} - ${rule.dataviewInfos.dataviewName ?? dataviewNotFoundLabel} (${rule.dataviewInfos.dateFormatLabel}) - ${rule.orderLabel}`;
        case "Custom Field":
            return `${rule.awardingRuleParameterTypeLabel} - ${rule.customDataFieldInfos.name} (${rule.customDataFieldInfos.dateFormatLabel}) - ${rule.orderLabel}`;
        case "By submitted request date and time":
            return `${rule.awardingRuleParameterTypeLabel} - ${rule.orderLabel}`;
    }
};

export const AdminDetailsTooltip = ({ data, translate, trigger = "hover", placement = "auto", children }) => {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings' });
    const { t: tError } = useTranslation('employmentBillboard', { keyPrefix: 'admin.warnings' });

    const jobTransferValidationLabels = {
        [JobTransferValidationType.PrimaryJobOnly]: t("primaryJobOnly"),
        [JobTransferValidationType.PrimaryJobAndTransferSet]: t("primaryJobAndTransferSet"),
      };

    const swapPeriodTypeLabels = {
        [SwapPeriodType.SameDay]: t("AdminRuleSet.swapPeriodSameDay"),
        [SwapPeriodType.SameWeek]: t("AdminRuleSet.swapPeriodSameWeek"),
        [SwapPeriodType.SamePayPeriod]: t("AdminRuleSet.swapPeriodSamePayPeriod"),
        [SwapPeriodType.SameSchedulePeriod]: t("AdminRuleSet.swapPeriodSameSchedulePeriod"),
      };

    // Sub-settings of a rule are excluded: a card lists one row per base rule, not the configuration inside it
    // (probationIsAllowToRequestOffers sits under On Probation, the consecutive-hours bounds under Consecutive hours).
    const excludedFields =
        ["id",
        "name",
        "isActive",
        "isEditDisplayed",
        "isDeleteDisabled",
        "isEditable",
        "probationIsAllowToRequestOffers",
        "isMinConsecutiveHours",
        "isMaxConsecutiveHours"];

    const flattenedData = flattenObject(data, t);
    const isAwardingRule = data.awardingRules && Array.isArray(data.awardingRules);
    const isBreakRule = data.breakRules && Array.isArray(data.breakRules);

    const awardingRuleContent = isAwardingRule ? (
        <div className="admin-tooltip-content">
            {data.awardingRules.map((rule, index) => (
                <div key={index} className="admin-tooltip-rule-row">
                    <div className="admin-tooltip-rule-label">
                        {index + 1}. {displayAwardingRuleProperties(rule, tError('dataviewNotFound'))}
                    </div>
                </div>
            ))}
        </div>
    ) : null;

    const breakRuleContent = isBreakRule ? (
        <div className="admin-tooltip-content">
            {data.breakRules.map((rule, index) => (
                <div key={index} className="admin-tooltip-rule-row">
                    <div className="admin-tooltip-rule-label">
                        {index + 1}. {t(translate + 'shiftDuration')} {t(translate + BreakRuleConditionTypeNames[rule.condition])} {getTimeString(parseFloat(rule.shiftDuration))} (hh:mm), {BreakRuleActionTypeNames[rule.action]} {getTimeString(parseFloat(rule.actionDuration))} (hh:mm) {t(translate + "break")}
                    </div>
                </div>
            ))}
        </div>
    ) : null;

    const defaultContent = (
        <div className="admin-tooltip-content">
            {flattenedData.requestTypeName && (
                <div className="admin-tooltip-row">
                    <div className="admin-tooltip-label">
                        {t("actionType")}:
                    </div>
                    <div className="admin-tooltip-value">
                        {flattenedData.requestTypeName}
                    </div>
                </div>
            )}

            {flattenedData.requestSubType && (
                <div className="admin-tooltip-row">
                    <div className="admin-tooltip-label">
                        {t("actionSubType")}:
                    </div>
                    <div className="admin-tooltip-value">
                        {flattenedData.requestSubType}
                    </div>
                </div>
            )}

            {flattenedData.jobTransferValidationType != null ? (<div className="admin-tooltip-row">
                <div className="admin-tooltip-label">
                    {t("qualification")}:
                </div>
                <div className="admin-tooltip-value">
                    {jobTransferValidationLabels[flattenedData.jobTransferValidationType] || null}
                </div>
            </div>) : null}

            {flattenedData.swapPeriodType != null ? (<div className="admin-tooltip-row">
                <div className="admin-tooltip-label">
                    {t(translate + "swapPeriodType")}:
                </div>
                <div className="admin-tooltip-value">
                    {swapPeriodTypeLabels[flattenedData.swapPeriodType] || null}
                </div>
            </div>) : null}


            {['breRuleSetName', 'awardingRuleName'].map((field) => (
                flattenedData[field] && (
                    <div key={field} className="admin-tooltip-row">
                        <div className="admin-tooltip-label">
                            {t(translate + field)}:
                        </div>
                        <div className="admin-tooltip-value">
                            {flattenedData[field]}
                        </div>
                    </div>
                )
            ))}
            {Object.keys(flattenedData)
                .filter(
                    (key) => !excludedFields.includes(key) &&
                        typeof flattenedData[key] === "boolean" &&
                        flattenedData[key] === true
                )
                .map((field) => (
                    <div key={field} className="admin-tooltip-row">
                        <div className="admin-tooltip-label">
                            {t(translate + field)}:
                        </div>
                        <div className="admin-tooltip-value">
                            {t('enabled')}
                        </div>
                    </div>
                ))}
        </div>
    );

    const popoverContent = (
        <Popover className="admin-tooltip-popover">
            <Typography variant="h6" className="admin-tooltip-title">
                {data.name}
            </Typography>
            {isAwardingRule ? awardingRuleContent : null}
            {isBreakRule ? breakRuleContent : null}
            {!isAwardingRule && !isBreakRule ? defaultContent : null}
        </Popover>
    );

    return (
        <Whisper
            trigger={trigger}
            placement={placement}
            speaker={popoverContent}
        >
            <span style={{ cursor: 'pointer', display: 'inline-block' }}>
                {children}
            </span>
        </Whisper>
    );
};
